import { useEffect, useState } from "react";
import { Loader2, Send, Plus, Banknote, ExternalLink, RefreshCw, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const money = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const CHIP = {
  paid: ["Paid ✓", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  processing: ["Processing", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  pending: ["Awaiting payment", "bg-[#1e3d3a]/8 text-[#1e3d3a]/60"],
  failed: ["Failed", "bg-[#b23a48]/12 text-[#b23a48]"],
};

export default function PortalInvoices() {
  const [conn, setConn] = useState(null);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to_name: "", to_email: "", amount: "", description: "" });

  const load = async () => {
    try {
      const [{ data: c }, { data: l }] = await Promise.all([
        api.get("/portal/invoices/connect-status"),
        api.get("/portal/invoices"),
      ]);
      setConn(c); setRows(l.invoices || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const setupPayouts = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/portal/invoices/connect", { origin_url: window.location.origin });
      if (data?.url) {
        window.open(data.url, "_blank", "noopener");
        toast.success("Opening Stripe to set up your payout account…");
        let n = 0; const iv = setInterval(async () => {
          n++; try { const { data: s } = await api.get("/portal/invoices/connect-status"); if (s.payouts_enabled) { clearInterval(iv); setConn(s); toast.success("Payout account ready ✓"); } } catch { /* keep polling */ }
          if (n >= 30) clearInterval(iv);
        }, 3000);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start payout setup."); }
    finally { setBusy(false); }
  };

  const create = async () => {
    if (!form.to_email.includes("@") || !(Number(form.amount) > 0)) { toast.error("Add a valid email and amount."); return; }
    setBusy(true);
    try {
      await api.post("/portal/invoices", { ...form, amount: Number(form.amount), origin_url: window.location.origin });
      setForm({ to_name: "", to_email: "", amount: "", description: "" });
      await load();
      toast.success("Payment request sent by email.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the request."); }
    finally { setBusy(false); }
  };

  const refresh = async (id) => { try { await api.get(`/portal/invoices/${id}/status`); await load(); } catch { /* ignore */ } };

  const ready = conn?.payouts_enabled;
  const inp = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none";

  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="portal-invoices">
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[#1e3d3a]"><Banknote className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Get paid — payment requests</h3></div>
        {ready && <span className="rounded-full bg-[#2f7d5b]/15 px-2.5 py-1 text-[10px] uppercase tracking-wide text-[#2f7d5b]">Payouts ready</span>}
      </div>
      <p className="text-xs text-[#1e3d3a]/55 mb-4">Bill another business by email — they pay by card or bank, and the money lands in your connected account.</p>

      {!ready ? (
        <div className="rounded-xl border border-[#c9a961]/40 bg-[#c9a961]/8 px-4 py-4">
          <p className="text-sm text-[#1e3d3a] mb-3">Set up your payout account once (secure Stripe onboarding) to start receiving payments.</p>
          <button data-testid="invoices-setup-payouts" onClick={setupPayouts} disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />} Set up payouts
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2 mb-4">
          <input data-testid="invoice-to-name" className={inp} placeholder="Business name" value={form.to_name} onChange={(e) => setForm({ ...form, to_name: e.target.value })} />
          <input data-testid="invoice-to-email" className={inp} placeholder="Their email" value={form.to_email} onChange={(e) => setForm({ ...form, to_email: e.target.value })} />
          <input data-testid="invoice-amount" className={inp} type="number" placeholder="Amount $" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          <input data-testid="invoice-desc" className={inp} placeholder="What for" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <button data-testid="invoice-send" onClick={create} disabled={busy}
            className="sm:col-span-4 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#c9a961] px-4 py-2 text-sm font-semibold text-[#1a2e2b] hover:bg-[#d8b972] disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send payment request
          </button>
        </div>
      )}

      {rows.length > 0 && (
        <ul className="divide-y divide-[#1e3d3a]/8" data-testid="invoices-list">
          {rows.map((r) => {
            const [label, cls] = CHIP[r.payment_status] || CHIP.pending;
            return (
              <li key={r.id} data-testid={`invoice-${r.id}`} className="flex items-center justify-between gap-3 py-2.5">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-[#1e3d3a] truncate">{r.to_name || r.to_email} · {money(r.amount)}</div>
                  <div className="text-[11px] text-[#1e3d3a]/50 truncate">{r.description}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <span className={`rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${cls}`}>{label}</span>
                  {r.payment_status !== "paid" && r.checkout_url && (
                    <a href={r.checkout_url} target="_blank" rel="noopener noreferrer" className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Open payment link"><ExternalLink className="h-3.5 w-3.5" /></a>
                  )}
                  {r.payment_status !== "paid"
                    ? <button data-testid={`invoice-refresh-${r.id}`} onClick={() => refresh(r.id)} className="p-1 text-[#1e3d3a]/40 hover:text-[#c9a961]" title="Check status"><RefreshCw className="h-3.5 w-3.5" /></button>
                    : <CheckCircle2 className="h-4 w-4 text-[#2f7d5b]" />}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
